// react
import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { sp } from '@/theme/mui';

// components
import { Option } from '@/components/atoms/Select/Select';
import { Title } from '@/components/atoms/Title/Title';

import { Editor } from '@/components/organisms/Editor/Editor';

import { FloatingActions } from '@/components/molecules/FloatingActions/FloatingActions';

// editor
import { createFieldHelper } from '@/components/organisms/EditorFields/fieldHelper';

// models
import {
  CareCircle,
  CareCircleMemberInvite,
  FieldType,
} from '@karehero/models';

/**
 * CareCircleInvite is an editor for creating a new care profile.
 */
export const CareCircleInvite = ({
  careCircle,
  onInvite,
  onCancel,
  relationshipOptions,
}: CareCircleInviteProps) => {
  // state
  const [value, setValue] = useState<CareCircleMemberInvite>({
    careCircleId: careCircle?.id,
    email: '',
    careRecipientAccountRelationshipId: '',
    firstName: '',
  });

  // memos
  const fields = useMemo(() => {
    return createFieldHelper<CareCircleMemberInvite>({
      firstName: {
        label: 'First name',
        field: {
          type: FieldType.Text,
        },
      },
      email: {
        label: 'Email',
        field: {
          type: FieldType.Email,
        },
      },
      careRecipientAccountRelationshipId: {
        label: 'Relationship to Care Recipient',
        field: {
          type: FieldType.Select,
          options: relationshipOptions,
        },
      },
    });
  }, [relationshipOptions]);

  return (
    <Wrapper>
      <Header>
        <Title>Invite to Care Circle</Title>
        <Description>
          Once invited your care circle member can join conversations with your
          care expert, view key information, and access the library resources.
        </Description>
      </Header>
      <Editor
        fields={fields}
        value={value}
        onChange={setValue}
        isActionsDisabled={true}
        isWithArrows
      />
      <ButtonWrapper>
        <FloatingActions
          actions={[
            {
              label: 'Cancel',
              onPress: onCancel,
              variant: 'secondary',
            },
            {
              label: 'Send email invite',
              onPress: () =>
                onInvite({ ...value, careCircleId: careCircle?.id }),
            },
          ]}
        />
      </ButtonWrapper>
    </Wrapper>
  );
};

const Wrapper = ({ children }: { children?: React.ReactNode }) => (
  <Stack sx={{ height: '100%', gap: sp[12], width: '100%' }}>{children}</Stack>
);
const Header = ({ children }: { children?: React.ReactNode }) => (
  <Stack>{children}</Stack>
);
const Description = ({ children }: { children?: React.ReactNode }) => (
  <Box sx={{ typography: 'headingXs', color: '#404042' }}>{children}</Box>
);
const ButtonWrapper = ({ children }: { children?: React.ReactNode }) => (
  <Box sx={{ position: 'relative' }}>{children}</Box>
);

interface CareCircleInviteProps {
  careCircle?: CareCircle;
  onInvite: (careCircleMemberInvite: CareCircleMemberInvite) => Promise<void>;
  onCancel: () => void;
  relationshipOptions: Option[];
}

CareCircleInvite.displayName = 'CareCircleInvite';

export default CareCircleInvite;
